import parse from "html-react-parser";
import { Key, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import Button from "../components/Button";
import FullscreenLoader from "../components/FullscreenLoader";
import RadioGroup from "../components/RadioGroup";
import { FormElement, FormElementProvider } from "../components/FormElement";
import { FormElementType, FormType, OptionType, RadiogroupElementType, SectionType } from "../helpers/types";
import { isElementVisible, formatRestrictedVisibility } from "../helpers/restrictedVisibility";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNewApplicationContext } from "../providers/NewApplicationProvider";
import UserInfo from "./UserInfo";

// Re-eksporteres for bakoverkompabilitet; nye importsteder bør bruke
// `../components/FormElement` direkte.
export { FormElement } from "../components/FormElement";

const Form = () => {
    const { application, section, form, setApplicationData } = useNewApplicationContext();
    const params = useParams();

    useEffect(() => {
        if (application === null) {
            setApplicationData(params.formType ?? '');
        }
    })

    if (!application || !section || !form) {
        return <FullscreenLoader />
    }


    if (section.type === 'pre') {
        return <PreQuestion />
    }

    // "user_info" var tidligere en SEKSJONS-type; nå er det en FORM-type.
    // Migrerte/eldre maler kan fortsatt ha user_info på seksjonsnivå — da skal
    // det KUN gjelde første skjema i seksjonen. Ellers rendres UserInfo på alle
    // skjemaene i seksjonen og søker kommer seg ikke videre.
    const isFirstFormInSection = (section as any).forms?.[0]?.url === form.url;
    const isUserInfo =
        form.type === 'user_info' ||
        (section.type === 'user_info' && isFirstFormInSection);

    if (isUserInfo) {
        return <UserInfo />
    }

    if (section.type === 'form' || section.type === 'user_info') {
        return <FormGroup />
    }

    return (
        <p>{section.type}</p>
    )
}

const PreQuestion = () => {
    const { formData, form, prev, next, updateForm, section, saveDraft, application } = useNewApplicationContext();

    const { t } = useTranslation();
    const [valid, setValid] = useState(false)
    const [selectedValue, setSelectedValue] = useState<OptionType | null>(null)

    const { title = "", elements = [] } = form || {};
    const { alternatives, name } = elements[0] as RadiogroupElementType;

    useEffect(() => {
        //setValid(false)
        //setSelectedValue(null)
    }, [form])

    useEffect(() => {
        if (section) {
            if (formData[name]) {
                const result = alternatives?.filter((obj: OptionType) => obj.value === formData[name])[0]
                setSelectedValue(result)
                // "Feil (rød)"-melding blokkerer videre navigasjon.
                setValid(result?.description?.type !== 'danger')
            } else {
                setValid(false)
                setSelectedValue(null)
            }
        }
    }, [formData, form]);

    if (!section) return <FullscreenLoader />

    const hasPreviousSection = () => {
        const sectionIndex = application.sections.findIndex((s: SectionType) => s.url === section.url);
        const prevSection = application.sections[sectionIndex - 1];
        return prevSection;
    }

    return (
        <>
            <h2 className="mb-4 mb-lg-5">{title}</h2>
            <RadioGroup
                items={alternatives}
                onCheck={(value) => updateForm(name, value)}
                checked={formData[name] ?? null}
                classes="mb-2 mg-lg-4"
                center
                newLine={true}
            />

            {valid && selectedValue?.description && (
                <div className={`alert alert-${selectedValue.description.type} text-start state-alert-success`} role="alert">
                    {parse(selectedValue.description.content)}
                </div>
            )}

            {hasPreviousSection() && (
                <Button
                    title={t('previous')}
                    leftIcon="arrow-left"
                    type="outline-primary"
                    onClick={prev}
                     classes="me-3"
                />
            )}

            {valid && (
                <Button
                    title={t('next')}
                    rightIcon="arrow-right"
                    type="primary"
                    onClick={() => {saveDraft(); next()}}
                />
            )}
        </>
    )
}

const FormGroup = () => {
    const {
        form,
        section,
        formData,
        application,
        debugBypass,
        schema,
        errors,
        updateForm,
        updateErrors,
        basePath,
    } = useNewApplicationContext();
    const navigate = useNavigate();
    const params = useParams();
    if (!form || !section) return false;
    const formIndex = section.forms.findIndex((s: FormType) => s.url === form.url)
    const isDev = import.meta.env.DEV;

    // Naviger til skjemadelen der et felt ligger (expensetable-penn på
    // forhåndsdefinerte rader). Slår opp feltets seksjon/skjema i malen.
    const navigateToField = (fieldName: string) => {
        if (!application) return;
        for (const sec of application.sections as SectionType[]) {
            const forms = (sec as any).forms ?? [];
            for (const f of forms) {
                if ((f.elements ?? []).some((el: FormElementType) => el.name === fieldName)) {
                    navigate(`${basePath}/${params.formType}/${sec.url}/${f.url}`);
                    return;
                }
            }
        }
    };

    return (
        <FormElementProvider
            value={{
                formData,
                schema,
                form,
                section,
                errors,
                updateForm,
                updateErrors,
                navigateToField,
            }}
        >
            <div className="my-4 my-lg-5">
                <h6>{section.title} - {formIndex + 1} / {section.forms.length}</h6>
                <h2>{form.title}</h2>
                {form.introtext && parse(form.introtext)}
            </div>

            <div className="row g-4">
                {form.elements.map((item: FormElementType, index: Key) => {
                    // "Begrenset visning" — skjul element hvis kriteriene fra
                    // form-builder ikke passerer mot dagens formData. Hidden-
                    // elementer skal aldri rendres siden de bare brukes til å
                    // bære verdier internt — vi sjekker derfor begrenset
                    // visning kun for synlige typer.
                    if (
                        item.type !== 'hidden' &&
                        !isElementVisible(item.restrictedVisibility, formData)
                    ) {
                        return null;
                    }

                    const hasRestricted = !!item.restrictedVisibility?.criteria?.length;
                    const showDebugBadge = isDev && debugBypass && hasRestricted && application;
                    const debugText = showDebugBadge
                        ? formatRestrictedVisibility(item.restrictedVisibility, application)
                        : '';

                    return (
                        <div className={item.display ?? "col-12"} key={index}>
                            {showDebugBadge && (
                                <div
                                    className="d-flex justify-content-end align-items-center text-info small mb-1"
                                    title={`Vises fordi ${debugText}`}
                                >
                                    <FontAwesomeIcon
                                        icon={['fal', 'circle-info']}
                                        className="me-1"
                                    />
                                    <span>Vises fordi {debugText}</span>
                                </div>
                            )}
                            <FormElement {...item}  />
                        </div>
                    );
                })}
            </div>
        </FormElementProvider>
    )
}

export default Form;
